/* ==========================================================================
   VoicePad: the fully customizable AI gadget
   Behaviour for templates/page.voicepad-gadget.json:
     - the floating glass-cube field (hero and sign-up)
     - the live keys on the product (hero) and the key mapper
     - the in-page demos: dictation, meeting, memo, your AI, Genspark
   Everything runs only while it is on screen and stops when it leaves.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.add('vpg-js');
  window.__vpgReady = true;

  var REDUCE = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESIGN = !!(window.Shopify && window.Shopify.designMode);
  var STOP = { stop: true };

  /* ---------------------------------------------------------------- utils */
  function $(sel, el) { return (el || doc).querySelector(sel); }
  function $$(sel, el) { return Array.prototype.slice.call((el || doc).querySelectorAll(sel)); }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function smooth(t) { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); }
  function hash(x, y) { var s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }

  function onScreen(el, enter, leave, threshold) {
    if (!('IntersectionObserver' in window)) { enter(); return null; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) enter(); else if (leave) leave();
      });
    }, { threshold: threshold == null ? 0.2 : threshold });
    io.observe(el);
    return io;
  }

  /* A run that can be cancelled: every wait checks it is still current. */
  function Runner() { this.gen = 0; }
  Runner.prototype.token = function () {
    var g = ++this.gen, self = this;
    return { alive: function () { return self.gen === g; } };
  };
  Runner.prototype.stop = function () { this.gen++; };

  function wait(tok, ms) {
    return new Promise(function (res, rej) {
      setTimeout(function () { if (tok.alive()) res(); else rej(STOP); }, ms);
    });
  }
  async function typeText(tok, el, text, speed) {
    for (var i = 1; i <= text.length; i++) {
      el.textContent = text.slice(0, i);
      var ch = text.charAt(i - 1);
      await wait(tok, speed + (ch === ' ' ? speed * 0.5 : 0) + (/[.,?!]/.test(ch) ? speed * 4 : 0));
    }
  }
  function swallow(e) { if (e !== STOP) { if (window.console) console.error(e); } }
  function inView(el) { var r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < (window.innerHeight || 0); }

  /* A looping demo that plays while visible. */
  function Demo(el, script, reset, final) {
    var run = new Runner(), playing = false, self = this;
    this.el = el;
    this.reset = reset;
    this.locked = false;
    this.play = function () {
      if (playing || self.locked) return;
      playing = true;
      var tok = run.token();
      (async function () {
        try {
          for (;;) { reset(); await script(tok); }
        } catch (e) { swallow(e); }
      })();
    };
    this.pause = function () { playing = false; run.stop(); reset(); };
    this.restart = function () { playing = false; run.stop(); self.play(); };
    this.isPlaying = function () { return playing; };
    reset();
    if (REDUCE) { if (final) final(); return; }
    this.io = onScreen(el, this.play, this.pause, 0.35);
  }
  Demo.prototype.destroy = function () { if (this.io) this.io.disconnect(); this.pause(); };

  /* ================================================================ field */
  var ICON_PATHS = [
    '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>',
    '<path d="M20 11.5a8 8 0 0 1-11.7 7.1L4 19.8l1.2-4A8 8 0 1 1 20 11.5z"/>',
    '<path d="M6 3h8.5L19 7.5V21H6z"/><path d="M14 3v5h5M9 12h6M9 16h6"/>',
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9.5h18M3 14.75h18M9 4v16"/>',
    '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8.5 20h7M12 16v4"/>',
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3z"/>',
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.4" fill="#fff" stroke="none"/>',
    '<rect x="3" y="4" width="18" height="16" rx="3"/><path d="M7.5 9.5l3 2.5-3 2.5M12.5 15h4"/>',
    '<path d="M4 20h4L19 9l-4-4L4 16z"/><path d="M13.5 6.5l4 4"/>',
    '<path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="14" width="4" height="6.5" rx="1.6"/><rect x="17" y="14" width="4" height="6.5" rx="1.6"/>',
    '<path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 10v4"/>',
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.6 14.9c1.6.8 2.7 2.6 3 5.1"/>'
  ];
  var ICON_IMGS = null;
  function iconImages() {
    if (ICON_IMGS) return ICON_IMGS;
    ICON_IMGS = ICON_PATHS.map(function (p) {
      var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg>';
      var im = new Image();
      im.decoding = 'async';
      im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      return im;
    });
    return ICON_IMGS;
  }

  /* level colours, light to brand blue, and how solid each level is */
  var RAMP = ['#ffffff', '#dceaff', '#bad7ff', '#8fc0ff', '#5fa6ff', '#2f8cff', '#0f7fff'];
  var LALPHA = [0, 0.34, 0.5, 0.64, 0.77, 0.87, 0.94];

  /* blob layouts: x, y (0..1 of the box), r (of the long side), a (strength), c (tide colour) */
  var LAYOUTS = {
    heroWide: [
      { x: 0.80, y: 0.46, r: 0.30, a: 1.00, c: '#0f7fff' },
      { x: 0.99, y: 0.92, r: 0.24, a: 0.95, c: '#2a63ff' },
      { x: 0.57, y: 1.04, r: 0.21, a: 0.70, c: '#5a6bff' },
      { x: 1.02, y: 0.04, r: 0.19, a: 0.80, c: '#3b94ff' },
      { x: 0.24, y: 1.08, r: 0.13, a: 0.45, c: '#7fb5ff' }
    ],
    heroTall: [
      { x: 0.52, y: 0.97, r: 0.50, a: 1.00, c: '#0f7fff' },
      { x: 1.04, y: 0.66, r: 0.30, a: 0.85, c: '#2a63ff' },
      { x: -0.06, y: 0.80, r: 0.30, a: 0.62, c: '#5a6bff' },
      { x: 0.98, y: 0.02, r: 0.20, a: 0.50, c: '#3b94ff' }
    ],
    endWide: [
      { x: 0.80, y: 0.52, r: 0.29, a: 1.00, c: '#0f7fff' },
      { x: 1.02, y: 0.08, r: 0.22, a: 0.85, c: '#2a63ff' },
      { x: 0.60, y: 1.02, r: 0.23, a: 0.72, c: '#5a6bff' }
    ],
    endTall: [
      { x: 0.55, y: 0.98, r: 0.46, a: 1.00, c: '#0f7fff' },
      { x: 1.04, y: 0.60, r: 0.28, a: 0.80, c: '#2a63ff' },
      { x: -0.04, y: 0.84, r: 0.26, a: 0.58, c: '#5a6bff' }
    ]
  };

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function Field(host) {
    var kind = host.getAttribute('data-vpg-field') || 'hero';
    var useIcons = host.getAttribute('data-icons') === '1';
    var section = host.parentNode;

    var tide = doc.createElement('canvas');
    var tiles = doc.createElement('canvas');
    tide.className = 'vpg-field__tide';
    tiles.className = 'vpg-field__tiles';
    host.appendChild(tide);
    host.appendChild(tiles);
    host.classList.add('is-live');
    var tctx = tide.getContext('2d');
    var fctx = tiles.getContext('2d');
    var base = doc.createElement('canvas');
    var bctx = base.getContext('2d');
    var canBlur = typeof fctx.filter === 'string';

    var W = 0, H = 0, S = 1, dpr = 1, G = 52, cols = 0, rows = 0, TW = 0, TH = 0;
    var layout = [], vals = null, levels = null;
    var sprites = [], hot = null, built = '';
    var visible = false, raf = 0, born = 0, introT = REDUCE ? 1 : 0;
    var last = 0, clock = 0, baseAt = -1e9;
    var pointer = { x: 0, y: 0, inside: false, touched: false };
    var field = { x: 0, y: 0, open: 0 };
    var ripples = [];
    var icons = useIcons ? iconImages() : null, iconLv = null, iconKey = '', iconTiles = [], clar = null, amb = null, ambAcc = 0;
    var self = this;

    function resize() {
      var r = host.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width));
      H = Math.max(1, Math.round(r.height));
      S = Math.max(W, H);
      dpr = Math.min(window.devicePixelRatio || 1, W < 600 ? 1.5 : 2);
      G = W < 600 ? 40 : W < 990 ? 46 : 52;
      tiles.width = base.width = Math.round(W * dpr);
      tiles.height = base.height = Math.round(H * dpr);
      TW = Math.max(60, Math.round(W / 8));
      TH = Math.max(60, Math.round(H / 8));
      tide.width = TW; tide.height = TH;
      cols = Math.ceil(W / G) + 1;
      rows = Math.ceil(H / G) + 1;
      vals = new Float32Array(cols * rows);
      levels = new Uint8Array(cols * rows);
      clar = new Float32Array(cols * rows);
      amb = new Float64Array(cols * rows);
      var wide = W >= 990 && W / H > 1.05;
      layout = LAYOUTS[(kind === 'end' ? 'end' : 'hero') + (wide ? 'Wide' : 'Tall')];
      buildSprites();
      baseAt = -1e9;
      if (!pointer.touched) { field.x = W * (wide ? 0.78 : 0.5); field.y = H * (wide ? 0.46 : 0.86); }
      if (!visible || REDUCE) draw(performance.now());
    }

    function buildSprites() {
      var key = G + ':' + dpr;
      if (key === built) return;
      built = key;
      var s = Math.max(4, Math.round(G * dpr));
      var gap = Math.max(1, Math.round(2.2 * dpr));
      var rad = 5 * dpr;
      sprites = RAMP.map(function (col) {
        var c = doc.createElement('canvas');
        c.width = c.height = s;
        var x = c.getContext('2d');
        x.fillStyle = col;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad);
        x.fill();
        var sheen = x.createLinearGradient(0, 0, s, s);
        sheen.addColorStop(0, 'rgba(255,255,255,0.30)');
        sheen.addColorStop(0.45, 'rgba(255,255,255,0.05)');
        sheen.addColorStop(1, 'rgba(255,255,255,0)');
        x.fillStyle = sheen;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad);
        x.fill();
        var shade = x.createLinearGradient(s, s, 0, 0);
        shade.addColorStop(0, 'rgba(8,30,80,0.12)');
        shade.addColorStop(0.5, 'rgba(8,30,80,0)');
        x.fillStyle = shade;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad);
        x.fill();
        x.strokeStyle = 'rgba(255,255,255,0.35)';
        x.lineWidth = Math.max(1, dpr * 0.8);
        roundRect(x, gap + 0.5 * dpr, gap + 0.5 * dpr, s - gap * 2 - dpr, s - gap * 2 - dpr, rad);
        x.stroke();
        return c;
      });
      var HS = 128;
      hot = doc.createElement('canvas');
      hot.width = hot.height = HS;
      var h = hot.getContext('2d');
      var hp = h.createRadialGradient(HS / 2, HS / 2, HS * 0.18, HS / 2, HS / 2, HS * 0.56);
      hp.addColorStop(0, 'rgba(255,255,255,0)');
      hp.addColorStop(0.6, 'rgba(255,255,255,0.12)');
      hp.addColorStop(1, 'rgba(255,255,255,0.5)');
      h.fillStyle = hp;
      roundRect(h, 0, 0, HS, HS, HS * 0.1);
      h.fill();
      var hs = h.createLinearGradient(0, 0, HS, HS);
      hs.addColorStop(0, 'rgba(255,255,255,0.38)');
      hs.addColorStop(0.5, 'rgba(255,255,255,0)');
      hs.addColorStop(1, 'rgba(8,30,80,0.2)');
      h.fillStyle = hs;
      roundRect(h, 0, 0, HS, HS, HS * 0.1);
      h.fill();
      iconKey = '';
    }

    function blobPos(b, i, t) {
      return {
        x: (b.x + Math.sin(t * (0.07 + i * 0.013) + i * 1.7) * 0.035) * W,
        y: (b.y + Math.cos(t * (0.055 + i * 0.011) + i * 2.3) * 0.04) * H,
        r: b.r * S * (1 + Math.sin(t * 0.09 + i) * 0.06)
      };
    }

    function landed(gx, gy) {
      if (introT >= 1) return 1;
      var d = ((cols - 1 - gx) / Math.max(1, cols)) * 0.6 + ((rows - 1 - gy) / Math.max(1, rows)) * 0.4;
      return smooth(introT * 1.9 - d * 0.85);
    }

    function rebuild(t) {
      /* tide: the soft blue light behind the glass */
      tctx.globalCompositeOperation = 'source-over';
      tctx.fillStyle = '#ffffff';
      tctx.fillRect(0, 0, TW, TH);
      var kx = TW / W, ky = TH / H;
      var P = layout.map(function (b, i) { return blobPos(b, i, t); });
      layout.forEach(function (b, i) {
        var p = P[i];
        var g = tctx.createRadialGradient(p.x * kx, p.y * ky, 0, p.x * kx, p.y * ky, p.r * 1.45 * kx);
        g.addColorStop(0, hexA(b.c, 0.62 * b.a));
        g.addColorStop(0.55, hexA(b.c, 0.22 * b.a));
        g.addColorStop(1, hexA(b.c, 0));
        tctx.fillStyle = g;
        tctx.fillRect(0, 0, TW, TH);
      });

      /* tiles: one value per cube, stepped into levels */
      for (var gx = 0; gx < cols; gx++) {
        for (var gy = 0; gy < rows; gy++) {
          var cx = gx * G + G / 2, cy = gy * G + G / 2, v = 0;
          for (var i = 0; i < P.length; i++) {
            var dx = (cx - P[i].x) / P[i].r, dy = (cy - P[i].y) / P[i].r;
            v += layout[i].a * Math.exp(-(dx * dx + dy * dy) * 1.9);
          }
          v = clamp(v, 0, 1);
          var k = gy * cols + gx;
          vals[k] = v;
          levels[k] = Math.min(6, Math.floor(v * 7.4));
        }
      }

      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.clearRect(0, 0, base.width, base.height);
      var s = G * dpr;
      for (gx = 0; gx < cols; gx++) {
        for (gy = 0; gy < rows; gy++) {
          var L = levels[gy * cols + gx];
          if (L < 1) continue;
          var a = LALPHA[L] * landed(gx, gy);
          if (a < 0.01) continue;
          bctx.globalAlpha = a;
          bctx.drawImage(sprites[L], gx * s, gy * s, s, s);
        }
      }
      bctx.globalAlpha = 1;
      pickIconTiles();
    }

    function hexA(hex, a) {
      var n = parseInt(hex.slice(1), 16);
      return 'rgba(' + (n >> 16 & 255) + ',' + (n >> 8 & 255) + ',' + (n & 255) + ',' + a.toFixed(3) + ')';
    }

    /* ---- icons hidden behind the frosted glass (hero only) ---- */
    function buildIcons() {
      if (!icons) return;
      var ready = icons.filter(function (im) { return im.complete && im.naturalWidth; }).length;
      var key = G + ':' + dpr + ':' + ready;
      if (key === iconKey) return;
      iconKey = key;
      var box = Math.round(G * 0.44 * dpr), pad = Math.ceil(6 * dpr * 2), side = box + pad * 2, LV = 5;
      iconLv = icons.map(function (im) {
        if (!(im.complete && im.naturalWidth)) return null;
        var out = [];
        for (var k = 0; k < LV; k++) {
          var r = 6 * dpr * k / (LV - 1);
          var c = doc.createElement('canvas');
          c.width = c.height = side;
          var x = c.getContext('2d');
          if (r < 0.1) x.drawImage(im, pad, pad, box, box);
          else if (canBlur) { x.filter = 'blur(' + r.toFixed(2) + 'px)'; x.drawImage(im, pad, pad, box, box); x.filter = 'none'; }
          else {
            var q = Math.max(3, Math.round(box / (1 + r * 0.9)));
            var tmp = doc.createElement('canvas');
            tmp.width = tmp.height = q;
            tmp.getContext('2d').drawImage(im, 0, 0, q, q);
            x.drawImage(tmp, 0, 0, q, q, pad - r * 0.5, pad - r * 0.5, box + r, box + r);
          }
          out.push(c);
        }
        out.side = side / dpr;
        return out;
      });
    }
    function pickIconTiles() {
      if (!icons) return;
      iconTiles = [];
      for (var gx = 0; gx < cols; gx++) {
        for (var gy = 0; gy < rows; gy++) {
          var k = gy * cols + gx;
          if (levels[k] < 4) continue;
          if (hash(gx * 1.7 + 3.1, gy * 2.3 + 7.7) > 0.38) continue;
          iconTiles.push({ k: k, gx: gx, gy: gy, w: vals[k] * landed(gx, gy), ic: Math.floor(hash(gx + 17.3, gy - 9.1) * icons.length) % icons.length });
        }
      }
    }
    function waveAt(x, y) {
      var v = 0, band = G * 1.5;
      for (var i = 0; i < ripples.length; i++) {
        var rp = ripples[i];
        var d = Math.sqrt((x - rp.x) * (x - rp.x) + (y - rp.y) * (y - rp.y));
        var b = 1 - Math.abs(d - rp.r) / band;
        if (b > 0) v = Math.max(v, b * (1 - rp.r / rp.max));
      }
      return v;
    }

    function draw(now) {
      var dt = Math.min(64, now - (last || now));
      last = now;
      if (!REDUCE) clock += dt / 1000;
      if (introT < 1 && born) introT = clamp((now - born - 200) / 1500, 0, 1);

      if (introT < 1 || now - baseAt > (W < 600 ? 120 : 85)) { baseAt = now; rebuild(clock); }

      /* where the light sits: the pointer if it is over us, otherwise a slow drift */
      var tx, ty, want = 1;
      if (pointer.inside) { tx = pointer.x; ty = pointer.y; }
      else {
        var t = now / 1000, wide = W >= 990 && W / H > 1.05;
        tx = W * (wide ? 0.74 + Math.sin(t * 0.43) * 0.17 : 0.5 + Math.sin(t * 0.43) * 0.32);
        ty = H * (wide ? 0.5 + Math.sin(t * 0.71 + 1.1) * 0.2 : 0.82 + Math.sin(t * 0.71 + 1.1) * 0.1);
        if (REDUCE) { tx = W * 0.72; ty = H * 0.5; }
      }
      var f = 1 - Math.pow(1 - 0.12, dt / 16.667);
      field.x += (tx - field.x) * f;
      field.y += (ty - field.y) * f;
      field.open += (want - field.open) * Math.min(1, dt / 260);

      for (var q = ripples.length - 1; q >= 0; q--) {
        ripples[q].r = (now - ripples[q].t0) * ripples[q].speed;
        if (ripples[q].r > ripples[q].max) ripples.splice(q, 1);
      }

      fctx.setTransform(1, 0, 0, 1, 0, 0);
      fctx.clearRect(0, 0, tiles.width, tiles.height);
      fctx.drawImage(base, 0, 0);
      fctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      /* bloom: cubes near the light rise and brighten */
      var R = (W < 600 ? 190 : 280) * field.open;
      var gx0 = Math.max(0, Math.floor((field.x - R) / G)), gx1 = Math.min(cols - 1, Math.floor((field.x + R) / G));
      var gy0 = Math.max(0, Math.floor((field.y - R) / G)), gy1 = Math.min(rows - 1, Math.floor((field.y + R) / G));
      for (var gx = gx0; gx <= gx1; gx++) {
        for (var gy = gy0; gy <= gy1; gy++) {
          var cx = gx * G + G / 2, cy = gy * G + G / 2;
          var n = Math.sqrt((cx - field.x) * (cx - field.x) + (cy - field.y) * (cy - field.y)) / R;
          if (n >= 1) continue;
          var l = 1 - n;
          l += 0.07 * Math.sin(n * Math.PI * 2.6 - clock * 2.2) * l;
          l += 0.12 * (hash(gx, gy) - 0.5) * 2 * l;
          l = smooth(l);
          var k = gy * cols + gx;
          var a = l * Math.max(vals[k], 0.3) * landed(gx, gy);
          if (a < 0.02) continue;
          var L = Math.min(6, Math.max(2, levels[k] + 1));
          var half = (G / 2) * (1 + 0.16 * l);
          fctx.globalAlpha = a;
          fctx.drawImage(sprites[L], cx - half, cy - half, half * 2, half * 2);
          if (l > 0.12) { fctx.globalAlpha = a * 0.9; fctx.drawImage(hot, cx - half, cy - half, half * 2, half * 2); }
        }
      }

      /* ripples light the glass they pass */
      if (ripples.length) {
        for (gx = 0; gx < cols; gx++) {
          for (gy = 0; gy < rows; gy++) {
            k = gy * cols + gx;
            if (vals[k] < 0.12) continue;
            var wx = gx * G + G / 2, wy = gy * G + G / 2, wv = waveAt(wx, wy);
            if (wv < 0.03) continue;
            var hh = (G / 2) * (1 + 0.12 * wv);
            fctx.globalAlpha = wv * vals[k] * 0.9;
            fctx.drawImage(sprites[Math.min(6, levels[k] + 1)], wx - hh, wy - hh, hh * 2, hh * 2);
            fctx.drawImage(hot, wx - hh, wy - hh, hh * 2, hh * 2);
          }
        }
      }

      /* icons behind the frost */
      if (icons) {
        buildIcons();
        if (!REDUCE && Math.random() < dt / 700 && iconTiles.length) {
          var pick = iconTiles[(Math.random() * iconTiles.length) | 0];
          amb[pick.k] = now + 1500 + Math.random() * 1500;
        }
        var thaw = 1 - Math.pow(1 - 0.16, dt / 16.667), freeze = 1 - Math.pow(1 - 0.05, dt / 16.667);
        for (var m = 0; m < iconTiles.length; m++) {
          var p = iconTiles[m], lv = iconLv && iconLv[p.ic];
          if (!lv) continue;
          var icx = p.gx * G + G / 2, icy = p.gy * G + G / 2;
          var near = R > 1 ? smooth(1 - Math.sqrt((icx - field.x) * (icx - field.x) + (icy - field.y) * (icy - field.y)) / R) : 0;
          var target = REDUCE ? (hash(p.gx + 5.5, p.gy + 1.3) < 0.35 ? 0.7 : 0)
            : Math.max(smooth((near - 0.2) / 0.55), now < amb[p.k] ? 1 : 0, ripples.length ? Math.min(1, waveAt(icx, icy) * 1.6) : 0);
          var c = clar[p.k];
          c += (target - c) * (target > c ? thaw : freeze);
          clar[p.k] = c;
          var alpha = p.w * (0.26 + 0.74 * c);
          if (alpha < 0.02) continue;
          var sc = (0.86 + 0.14 * c) * (1 + 0.16 * near);
          var sd = lv.side * sc;
          var fl = (1 - c) * (lv.length - 1), k0 = Math.floor(fl), k1 = Math.min(lv.length - 1, k0 + 1), tt = fl - k0;
          fctx.globalAlpha = alpha * (1 - tt);
          fctx.drawImage(lv[k0], icx - sd / 2, icy - sd / 2, sd, sd);
          if (tt > 0.01) { fctx.globalAlpha = alpha * tt; fctx.drawImage(lv[k1], icx - sd / 2, icy - sd / 2, sd, sd); }
        }
      }
      fctx.globalAlpha = 1;
      fctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    function frame(now) {
      raf = 0;
      if (!visible) return;
      draw(now);
      raf = requestAnimationFrame(frame);
    }
    function start() {
      if (visible) return;
      visible = true;
      if (!born) born = performance.now();
      last = 0;
      if (REDUCE) { draw(performance.now()); return; }
      if (!raf) raf = requestAnimationFrame(frame);
    }
    function stop() { visible = false; if (raf) cancelAnimationFrame(raf); raf = 0; }

    this.ripple = function (clientX, clientY, big) {
      if (REDUCE) return;
      var r = host.getBoundingClientRect();
      ripples.push({ x: clientX - r.left, y: clientY - r.top, t0: performance.now(), r: 0, speed: big ? 1.1 : 0.85, max: big ? Math.sqrt(W * W + H * H) : G * 8 });
      if (ripples.length > 6) ripples.shift();
    };

    function move(e) {
      var r = host.getBoundingClientRect();
      var x = e.clientX - r.left, y = e.clientY - r.top;
      pointer.inside = x >= 0 && y >= 0 && x <= r.width && y <= r.height && e.pointerType !== 'touch';
      if (pointer.inside) { pointer.x = x; pointer.y = y; pointer.touched = true; }
    }
    function tap(e) {
      var r = host.getBoundingClientRect();
      if (e.clientY < r.top || e.clientY > r.bottom) return;
      var t = e.target;
      if (t && t.closest && t.closest('a,button,input,label,select,textarea,form,.vpg-dev')) return;
      self.ripple(e.clientX, e.clientY, false);
    }
    window.addEventListener('pointermove', move, { passive: true });
    section.addEventListener('pointerdown', tap, { passive: true });
    doc.addEventListener('mouseleave', function () { pointer.inside = false; });

    var ro = 'ResizeObserver' in window ? new ResizeObserver(function () { resize(); }) : null;
    if (ro) ro.observe(host); else window.addEventListener('resize', resize);
    resize();
    this.io = onScreen(host, start, stop, 0);
    doc.addEventListener('visibilitychange', function () { if (doc.hidden) stop(); else if (host.getBoundingClientRect().bottom > 0 && host.getBoundingClientRect().top < innerHeight) start(); });
    this.destroy = function () { stop(); if (this.io) this.io.disconnect(); if (ro) ro.disconnect(); window.removeEventListener('pointermove', move); };
  }

  /* ========================================================= live keys */
  var POOLS = [
    ['Agent', 'ChatGPT', 'Claude', 'Gemini', 'Grok'],
    ['Meeting', 'Terminal', 'Rewrite'],
    ['Translate', 'Polish', 'Rewrite'],
    ['Memo', 'Hands-free'],
    ['Dictate', 'Push to talk']
  ];

  function setLegend(key, text, animate) {
    var el = $('[data-legend]', key);
    if (!el || el.textContent === text) return;
    el.textContent = text;
    if (animate && !REDUCE) {
      key.classList.remove('is-flip');
      void key.offsetWidth;
      key.classList.add('is-flip');
    }
  }

  /* hero: keys light up on their own and now and then change jobs */
  function HeroKeys(dev, field) {
    var keys = $$('.vpg-key', dev);
    if (!keys.length) return null;
    var order = [4, 0, 1, 2, 3], step = 0, timer = 0, visible = false, hover = -1;
    var pos = keys.map(function (k, i) {
      var cur = $('[data-legend]', k).textContent.trim(), at = POOLS[i].indexOf(cur);
      if (at < 0) { POOLS[i] = [cur].concat(POOLS[i]); at = 0; }
      return at;
    });
    function light(i, ms) {
      keys[i].classList.add('is-down');
      setTimeout(function () { keys[i].classList.remove('is-down'); keys[i].classList.add('is-lit'); }, 110);
      setTimeout(function () { if (hover !== i) keys[i].classList.remove('is-lit'); }, ms || 1150);
    }
    function next(i) {
      pos[i] = (pos[i] + 1) % POOLS[i].length;
      setLegend(keys[i], POOLS[i][pos[i]], true);
    }
    function tick() {
      if (!visible) return;
      var i = order[step % order.length];
      light(i);
      if (step >= 5 && step % 2 === 1) setTimeout(function () { next(i); }, 260);
      step++;
      timer = setTimeout(tick, 1450);
    }
    keys.forEach(function (k, i) {
      k.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hover = i; k.classList.add('is-lit'); } });
      k.addEventListener('pointerleave', function () { if (hover === i) hover = -1; k.classList.remove('is-lit'); });
      k.addEventListener('click', function () {
        light(i, 900);
        next(i);
        if (field) { var r = k.getBoundingClientRect(); field.ripple(r.left + r.width / 2, r.top + r.height / 2, true); }
      });
    });
    if (REDUCE) { keys[4].classList.add('is-lit'); return null; }
    var io = onScreen(dev, function () { if (visible) return; visible = true; timer = setTimeout(tick, 900); },
      function () { visible = false; clearTimeout(timer); }, 0.1);
    return { destroy: function () { visible = false; clearTimeout(timer); if (io) io.disconnect(); } };
  }

  /* the key mapper: pick a key, then give it a job */
  function KeyLab(lab) {
    var dev = $('.vpg-dev', lab);
    if (!dev) return null;
    var keys = $$('.vpg-key', dev);
    var chips = $$('.vpg-chip', lab);
    var row = $('[data-chips]', lab);
    var legends = keys.map(function (k) { return $('[data-legend]', k).textContent.trim(); });
    var sel = 0, idleAt = 0, timer = 0, visible = false, auto = !REDUCE;

    /* on phones the jobs sit in one scrolling row: bring the current one into view */
    function showChip() {
      if (!row || row.scrollWidth <= row.clientWidth + 2) return;
      var c = chips.filter(function (x) { return x.getAttribute('data-job') === legends[sel]; })[0];
      if (!c) return;
      var left = c.offsetLeft - (row.clientWidth - c.offsetWidth) / 2;
      try { row.scrollTo({ left: Math.max(0, left), behavior: REDUCE ? 'auto' : 'smooth' }); } catch (e) { row.scrollLeft = left; }
    }

    function paint() {
      keys.forEach(function (k, i) {
        k.classList.toggle('is-sel', i === sel);
        k.setAttribute('aria-pressed', i === sel ? 'true' : 'false');
      });
      chips.forEach(function (c) {
        var job = c.getAttribute('data-job');
        c.classList.toggle('is-here', job === legends[sel]);
        c.classList.toggle('is-used', legends.indexOf(job) > -1 && job !== legends[sel]);
        c.setAttribute('aria-pressed', job === legends[sel] ? 'true' : 'false');
      });
    }
    function glow(i, ms) {
      keys[i].classList.add('is-lit');
      clearTimeout(keys[i]._t);
      keys[i]._t = setTimeout(function () { keys[i].classList.remove('is-lit'); }, ms || 900);
    }
    function select(i, follow) { sel = i; paint(); glow(i, 700); if (follow) showChip(); }
    function assign(job, follow) {
      var from = legends.indexOf(job);
      if (from === sel) { glow(sel, 700); return; }
      if (from > -1) { legends[from] = legends[sel]; setLegend(keys[from], legends[from], true); }
      legends[sel] = job;
      setLegend(keys[sel], job, true);
      glow(sel, 1100);
      paint();
      if (follow) showChip();
    }
    function touch() { idleAt = Date.now(); }
    keys.forEach(function (k, i) { k.addEventListener('click', function () { touch(); select(i, true); }); });
    chips.forEach(function (c) { c.addEventListener('click', function () { touch(); assign(c.getAttribute('data-job'), false); }); });
    if (row) row.addEventListener('pointerdown', touch, { passive: true });

    function tick() {
      if (!visible) return;
      if (auto && Date.now() - idleAt > 9000) {
        var i = (sel + 1) % keys.length;
        select(i);
        setTimeout(function () {
          if (!visible || Date.now() - idleAt < 9000) return;
          var free = chips.map(function (c) { return c.getAttribute('data-job'); }).filter(function (j) { return legends.indexOf(j) < 0; });
          if (free.length) assign(free[(Math.random() * free.length) | 0], true);
        }, 650);
      }
      timer = setTimeout(tick, 2600);
    }
    paint();
    var io = onScreen(lab, function () { if (visible) return; visible = true; timer = setTimeout(tick, 1400); },
      function () { visible = false; clearTimeout(timer); }, 0.25);
    return { destroy: function () { visible = false; clearTimeout(timer); if (io) io.disconnect(); } };
  }

  /* ============================================================== demos */
  function keyPress(wrap, tok, ms) {
    if (!wrap) return Promise.resolve();
    wrap.classList.add('is-down');
    return wait(tok, ms || 260).then(function () { wrap.classList.remove('is-down'); });
  }
  function keyHold(wrap, on) { if (wrap) wrap.classList.toggle('is-down', on); }

  /* 1. dictation: messy speech in, clean text out */
  function Dictate(stage) {
    var out = $('[data-out]', stage), raw = $('[data-raw]', stage), hud = $('[data-hud]', stage);
    var km = $('[data-keymap]', stage), hold = $('[data-hold]', stage);
    var WORDS = [['um', 1], ['so', 1], ['the'], ['deck'], ['is,'], ['uh', 1], ['ready.'], ['Can'], ['you'], ['check'], ['slide'], ['four'], ['by'], ['Friday?'], ['Thanks!']];
    var CLEAN = 'The deck is ready. Can you check slide 4 by Friday? Thanks!';

    function reset() {
      out.textContent = ''; raw.innerHTML = '';
      hud.className = 'vpg-hud';
      stage.classList.remove('is-typing', 'is-ready');
      keyHold(km, false);
      if (hold) hold.classList.remove('is-held');
    }
    function addWord(w) {
      var s = doc.createElement('span');
      s.className = 'w' + (w[1] ? ' is-fill' : '');
      s.textContent = w[0];
      raw.appendChild(s);
    }
    async function finish(tok) {
      keyHold(km, false);
      hud.classList.remove('is-live');
      $$('.w.is-fill', raw).forEach(function (w) { w.classList.add('is-gone'); });
      await wait(tok, 650);
      hud.classList.add('is-done');
      await wait(tok, 650);
      hud.classList.remove('is-on');
      stage.classList.add('is-typing');
      await typeText(tok, out, CLEAN, 24);
    }
    async function script(tok) {
      stage.classList.add('is-ready');
      await wait(tok, 1000);
      keyHold(km, true);
      hud.classList.add('is-on', 'is-live');
      for (var i = 0; i < WORDS.length; i++) {
        addWord(WORDS[i]);
        await wait(tok, WORDS[i][1] ? 330 : 210);
      }
      await wait(tok, 350);
      await finish(tok);
      await wait(tok, 3200);
    }
    var demo = new Demo(stage, script, reset, function () { stage.classList.add('is-ready'); out.textContent = CLEAN; });

    /* "Hold to talk": the visitor plays the key */
    if (hold && REDUCE) hold.hidden = true;
    if (hold && !REDUCE) {
      var manual = new Runner(), holding = false, said = 0;
      function down(e) {
        if (e.button > 0) return;
        e.preventDefault();
        if (hold.setPointerCapture && e.pointerId != null) { try { hold.setPointerCapture(e.pointerId); } catch (err) {} }
        demo.pause();
        demo.locked = true;
        holding = true; said = 0;
        hold.classList.add('is-held');
        stage.classList.add('is-ready');
        keyHold(km, true);
        hud.classList.add('is-on', 'is-live');
        var tok = manual.token();
        (async function () {
          try {
            while (holding && said < WORDS.length) { addWord(WORDS[said++]); await wait(tok, 230); }
          } catch (err) { swallow(err); }
        })();
      }
      function up() {
        if (!holding) return;
        holding = false;
        hold.classList.remove('is-held');
        var tok = manual.token();
        (async function () {
          try {
            while (said < WORDS.length) { addWord(WORDS[said++]); await wait(tok, 45); }
            await wait(tok, 200);
            await finish(tok);
            await wait(tok, 4200);
            reset();
            demo.locked = false;
            if (inView(stage)) demo.play();
          } catch (err) { swallow(err); }
        })();
      }
      hold.addEventListener('pointerdown', down);
      hold.addEventListener('pointerup', up);
      hold.addEventListener('pointercancel', up);
      hold.addEventListener('lostpointercapture', up);
      hold.addEventListener('contextmenu', function (e) { e.preventDefault(); });
      hold.addEventListener('keydown', function (e) { if ((e.key === ' ' || e.key === 'Enter') && !e.repeat && !holding) { e.preventDefault(); down({ button: 0, preventDefault: function () {} }); } });
      hold.addEventListener('keyup', function (e) { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); up(); } });
    }
    return demo;
  }

  /* 2. meeting: record on the device, notes at the end */
  function Meeting(stage) {
    var win = $('.vpg-meet', stage), clock = $('[data-clock]', stage), state = $('[data-state]', stage), km = $('[data-keymap]', stage);
    var lines = $$('[data-line]', stage), blocks = $$('[data-n]', stage), todos = $$('[data-todo]', stage);
    function fmt(s) { var m = Math.floor(s / 60), r = Math.floor(s % 60); return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r; }
    function reset() {
      win.classList.remove('is-notes', 'is-live');
      lines.concat(blocks).forEach(function (l) { l.classList.remove('is-in'); });
      todos.forEach(function (t) { t.classList.remove('is-done'); });
      clock.textContent = '00:00';
      state.textContent = 'Tap to record';
      state.classList.remove('is-hot');
      keyHold(km, false);
    }
    async function script(tok) {
      await wait(tok, 900);
      await keyPress(km, tok);
      state.textContent = 'Recording'; state.classList.add('is-hot');
      win.classList.add('is-live');
      var t0 = performance.now(), RUN = 5600, END = 31 * 60 + 12;
      var timer = setInterval(function () {
        if (!tok.alive()) { clearInterval(timer); return; }
        var p = clamp((performance.now() - t0) / RUN, 0, 1);
        clock.textContent = fmt(END * (p * p * 0.35 + p * 0.65));
      }, 60);
      try {
        await wait(tok, 700); lines[0].classList.add('is-in');
        await wait(tok, 1500); lines[1].classList.add('is-in');
        await wait(tok, 1500); lines[2].classList.add('is-in');
        await wait(tok, 1900);
      } finally { clearInterval(timer); }
      clock.textContent = fmt(END);
      await keyPress(km, tok);
      win.classList.remove('is-live');
      state.textContent = 'Writing notes';
      await wait(tok, 700);
      win.classList.add('is-notes');
      state.textContent = 'Notes ready';
      for (var i = 0; i < blocks.length; i++) { blocks[i].classList.add('is-in'); await wait(tok, 220); }
      await wait(tok, 500);
      for (i = 0; i < todos.length; i++) { todos[i].classList.add('is-done'); await wait(tok, 650); }
      await wait(tok, 3000);
    }
    return new Demo(stage, script, reset, function () {
      win.classList.add('is-notes');
      blocks.forEach(function (b) { b.classList.add('is-in'); });
      todos[0].classList.add('is-done');
      state.textContent = 'Notes ready';
    });
  }

  /* 3. memo: say it now, ask for it later */
  function Memo(stage) {
    var rec = $('[data-rec]', stage), live = $('[data-live]', stage), item = $('[data-new]', stage), when = $('[data-when]', stage);
    var later = $('[data-later]', stage), ask = $('[data-ask]', stage), q = $('[data-q]', stage), answer = $('[data-answer]', stage);
    var state = $('[data-state]', stage), km = $('[data-keymap]', stage);
    var MEMO = 'Open the launch email with the customer story.';
    var QUESTION = 'What was my idea for the launch email?';
    function reset() {
      rec.classList.remove('is-on', 'is-live'); live.textContent = '';
      item.classList.remove('is-in', 'is-hi'); when.textContent = 'Memo · Just now';
      later.classList.remove('is-on');
      ask.classList.remove('is-focus', 'is-typing'); q.textContent = '';
      answer.classList.remove('is-open');
      state.textContent = 'Tap and talk'; state.classList.remove('is-hot');
      keyHold(km, false);
    }
    async function script(tok) {
      await wait(tok, 900);
      await keyPress(km, tok);
      state.textContent = 'Recording'; state.classList.add('is-hot');
      rec.classList.add('is-on', 'is-live');
      var words = MEMO.split(' ');
      for (var i = 1; i <= words.length; i++) { live.textContent = words.slice(0, i).join(' '); await wait(tok, 240); }
      await wait(tok, 500);
      await keyPress(km, tok);
      rec.classList.remove('is-live');
      await wait(tok, 250);
      rec.classList.remove('is-on');
      item.classList.add('is-in', 'is-hi');
      state.textContent = 'Saved'; state.classList.remove('is-hot');
      await wait(tok, 1500);
      item.classList.remove('is-hi');
      await wait(tok, 400);
      later.classList.add('is-on');
      state.textContent = 'Two weeks later';
      await wait(tok, 700);
      when.textContent = 'Memo · 2 weeks ago';
      await wait(tok, 900);
      later.classList.remove('is-on');
      await wait(tok, 400);
      ask.classList.add('is-focus');
      state.textContent = 'Ask';
      await wait(tok, 350);
      ask.classList.add('is-typing');
      await typeText(tok, q, QUESTION, 30);
      await wait(tok, 450);
      ask.classList.remove('is-focus');
      answer.classList.add('is-open');
      item.classList.add('is-hi');
      state.textContent = 'Found it'; state.classList.add('is-hot');
      await wait(tok, 3600);
    }
    return new Demo(stage, script, reset, function () {
      item.classList.add('is-in', 'is-hi');
      when.textContent = 'Memo · 2 weeks ago';
      ask.classList.add('is-typing');
      q.textContent = QUESTION;
      answer.classList.add('is-open');
      state.textContent = 'Found it';
    });
  }

  /* 4. your AI: one key per assistant */
  var AI_PROMPTS = [
    'Draft a friendly reply saying Thursday at 3 works for me.',
    'Summarize this contract and list the three biggest risks.',
    'Plan three days in Kyoto with one great dinner each night.',
    'What are people saying about the launch today?'
  ];
  function AiDemo(box) {
    var keys = $$('.vpg-aikey', box), name = $('[data-ai-name]', box), opened = $('[data-opened]', box);
    var hello = $('[data-hello]', box), msg = $('[data-msg]', box), sent = $('[data-sent]', box), reply = $('[data-reply]', box);
    var input = $('[data-input]', box), typed = $('[data-typed]', box);
    var names = keys.map(function (k) { return $('.vpg-aikey__name', k).textContent.trim(); });
    var at = 0;
    function clearChat() {
      hello.classList.remove('is-gone'); msg.classList.remove('is-on'); reply.classList.remove('is-on');
      typed.textContent = '';
      input.classList.remove('is-live', 'is-typing', 'is-ready');
    }
    function reset() {
      clearChat();
      keys.forEach(function (k) { k.classList.remove('is-lit'); k.setAttribute('aria-pressed', 'false'); });
      opened.classList.remove('is-on');
    }
    async function show(tok, i) {
      keys.forEach(function (k, j) { k.classList.toggle('is-lit', j === i); k.setAttribute('aria-pressed', j === i ? 'true' : 'false'); });
      opened.classList.remove('is-on');
      name.classList.add('is-swap');
      await wait(tok, 200);
      clearChat();
      name.textContent = names[i];
      name.classList.remove('is-swap');
      await wait(tok, 250);
      opened.classList.add('is-on');
      await wait(tok, 450);
      input.classList.add('is-live', 'is-typing');
      await typeText(tok, typed, AI_PROMPTS[i % AI_PROMPTS.length], 26);
      input.classList.add('is-ready');
      await wait(tok, 500);
      input.classList.remove('is-live', 'is-typing', 'is-ready');
      sent.textContent = typed.textContent;
      typed.textContent = '';
      hello.classList.add('is-gone');
      msg.classList.add('is-on');
      await wait(tok, 550);
      reply.classList.add('is-on');
      await wait(tok, 2300);
      keys[i].classList.remove('is-lit');
    }
    async function script(tok) {
      for (;;) { await show(tok, at); at = (at + 1) % keys.length; await wait(tok, 250); }
    }
    function still(i) {
      reset();
      keys[i].classList.add('is-lit');
      keys[i].setAttribute('aria-pressed', 'true');
      name.textContent = names[i];
      opened.classList.add('is-on');
      sent.textContent = AI_PROMPTS[i % AI_PROMPTS.length];
      hello.classList.add('is-gone');
      msg.classList.add('is-on');
      reply.classList.add('is-on');
    }
    var demo = new Demo(box, script, reset, function () { still(0); });
    keys.forEach(function (k, i) {
      k.addEventListener('click', function () {
        at = i;
        if (REDUCE) still(i); else demo.restart();
      });
    });
    return demo;
  }

  /* 5. Genspark: a sentence in, finished work out */
  var GS_RUNS = [
    { cmd: 'Make a 6-slide deck on our Q3 results.', out: 'deck', steps: ['Planning the outline', 'Gathering the numbers', 'Building the charts', 'Designing the slides'] },
    { cmd: 'Compare our top five competitors in a sheet.', out: 'sheet', steps: ['Finding the competitors', 'Collecting prices and ratings', 'Building the table', 'Writing a summary'] }
  ];
  function GsDemo(box) {
    var cmd = $('[data-cmd]', box), go = $('[data-go]', box), steps = $$('[data-step]', box), outs = $$('[data-out]', box), kinds = $$('[data-kind]', box);
    var n = 0;
    function reset() {
      box.classList.remove('is-live', 'is-working');
      cmd.textContent = '';
      go.classList.remove('is-on', 'is-press');
      steps.forEach(function (s, i) { s.classList.remove('is-run', 'is-done'); $('[data-step-text]', s).textContent = GS_RUNS[n].steps[i]; });
      outs.forEach(function (o) { o.classList.remove('is-on'); });
      kinds.forEach(function (k) { k.classList.toggle('is-on', k.getAttribute('data-kind') === GS_RUNS[n].out); });
    }
    async function script(tok) {
      var run = GS_RUNS[n];
      await wait(tok, 700);
      box.classList.add('is-live');
      await typeText(tok, cmd, run.cmd, 30);
      box.classList.remove('is-live');
      go.classList.add('is-on');
      await wait(tok, 380);
      go.classList.add('is-press');
      await wait(tok, 160);
      go.classList.remove('is-press');
      box.classList.add('is-working');
      for (var i = 0; i < steps.length; i++) {
        steps[i].classList.add('is-run');
        await wait(tok, 640 + i * 60);
        steps[i].classList.remove('is-run');
        steps[i].classList.add('is-done');
      }
      box.classList.remove('is-working');
      outs.forEach(function (o) { o.classList.toggle('is-on', o.getAttribute('data-out') === run.out); });
      await wait(tok, 3800);
      n = (n + 1) % GS_RUNS.length;
    }
    return new Demo(box, script, reset, function () {
      cmd.textContent = GS_RUNS[0].cmd;
      go.classList.add('is-on');
      steps.forEach(function (s) { s.classList.add('is-done'); });
      outs.forEach(function (o) { o.classList.toggle('is-on', o.getAttribute('data-out') === 'deck'); });
    });
  }

  /* ============================================================ wiring */
  var registry = [];

  function reveal(scope) {
    var els = $$('.vpg-reveal', scope);
    if (DESIGN || REDUCE || !('IntersectionObserver' in window)) { els.forEach(function (e) { e.classList.add('is-in'); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach(function (e) { io.observe(e); });
  }

  function nav() {
    var bar = $('[data-vpg-nav]');
    if (!bar || bar._vpg) return;
    bar._vpg = 1;
    var on = false, tone = '', queued = false;
    function check() {
      queued = false;
      var want = (window.scrollY || window.pageYOffset) > 8;
      if (want !== on) { on = want; bar.classList.toggle('is-solid', on); }
      var mid = bar.offsetHeight / 2, t = '';
      $$('[data-vpg-tone]').some(function (s) {
        var r = s.getBoundingClientRect();
        if (r.top <= mid && r.bottom > mid) { t = s.getAttribute('data-vpg-tone'); return true; }
        return false;
      });
      if (t !== tone) {
        bar.classList.toggle('is-dark', t === 'dark');
        bar.classList.toggle('is-blue', t === 'blue');
        tone = t;
      }
    }
    function queue() { if (!queued) { queued = true; requestAnimationFrame(check); } }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    check();
  }

  function init(scope) {
    scope = scope || doc;
    nav();
    reveal(scope);
    var heroField = null;
    $$('[data-vpg-field]', scope).forEach(function (el) {
      if (el._vpg) return;
      el._vpg = 1;
      var f = new Field(el);
      registry.push({ el: el, obj: f });
      if (el.getAttribute('data-vpg-field') === 'hero') heroField = f;
    });
    $$('[data-vpg-device="show"]', scope).forEach(function (el) {
      if (el._vpg) return;
      el._vpg = 1;
      registry.push({ el: el, obj: HeroKeys(el, heroField) });
    });
    $$('[data-vpg-keys]', scope).forEach(function (el) {
      if (el._vpg) return;
      el._vpg = 1;
      registry.push({ el: el, obj: KeyLab(el) });
    });
    var map = { dictate: Dictate, meeting: Meeting, memo: Memo, ai: AiDemo, genspark: GsDemo };
    $$('[data-vpg-demo]', scope).forEach(function (el) {
      if (el._vpg) return;
      el._vpg = 1;
      var make = map[el.getAttribute('data-vpg-demo')];
      if (make) registry.push({ el: el, obj: make(el) });
    });
  }

  function teardown(scope) {
    registry = registry.filter(function (r) {
      if (scope.contains(r.el)) { if (r.obj && r.obj.destroy) r.obj.destroy(); return false; }
      return true;
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { init(doc); });
  else init(doc);

  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });
  doc.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
})();
